import {
  TimeFormat,
  HelpTypes,
  PendingCancellationStatus,
  WorkshiftStatus,
  PharmacyLegendVariant,
  PharmacistLegendVariant,
  AdminTypes,
  PermissionsOfAdmin,
  PharmacyFilterWorkshifts,
  BroadcastNotificationType,
  LogActionTypes,
  PerformerTypes,
  ActivityLogActionTypes,
  ActivityLogModuleTypes,
  Language,
  BookingState,
  MiscType,
  PharmacyLegendIntermediate,
  ProfessionalReplacementTypes,
  Agencies,
  WorkshiftReportStatus,
} from '../helpers/Constants';
import {
  IAvailabilityModel,
  IBackendDropdown,
  IGeneralPaginationResponse,
  IGenericPagingApiParams,
  ILongTermContractModel,
  IReviewDetails,
  ISoftwareResponse,
} from './Models';
import {
  IAllLanguages,
  IContactPreferences,
  IFilterSoftware,
  IMap,
  IPharmacistCapacity,
  IProvinces,
  IRegions,
  IVolumes,
} from './PharmacistModels';
import {
  ILongContractAttributes,
  ILongContractRequestsData,
  IPharmacistCapacityDetails,
  IProfessionalCapacityDetails,
  IProfessionalRating,
  IUpdateLongTermShiftContract,
} from './PharmacyModels';
import { IProfessionalCapacity } from './ProfessionalModels';

export interface IAdminCalendarParams {
  startDate: string;
  endDate: string;
  softwareId?: string;
}

export interface GenericPaginationAPIParams {
  paging: {
    page: number;
    sortBy?: string;
    desc?: boolean;
  };
}

export interface GenericPaginationResponse {
  page: number;
  itemPerPage: number;
  totalCount: number;
}

export interface GenericPageWithSort {
  page: number;
  sortBy?: string;
  desc?: boolean;
}

export type ICalendarEventType =
  | `${PharmacyLegendVariant}`
  | `${PharmacistLegendVariant}`
  | `${AdminLegendVariant}`
  | `${PharmacyLegendIntermediate}`;

export interface GenericPaginationModelAPIParams {
  pagingModel: {
    page: number;
    itemPerPage?: number;
    sortBy?: string;
    desc?: boolean;
  };
}

export interface IPharmacyAdminPharmacies {
  userId: string;
  id: string;
  userName: string;
  email: string;
  logoId: string;
}

export interface IAdminCalendar {
  id: string;
  type: string;
  bookingId: string;
  workShiftId: string;
  startDate: string;
  endDate: string;
  logoId: string;
  city: string;
  softwareName: string;
  hourlyRate: number;
  isMatchingAvailabilityExist: boolean;
  pharmacyName: string;
  longTermShiftContractId?: string;
}

export interface IAdminActiveUsersData {
  id: string;
  userId: string;
  name: string;
  city: string;
  logoId: string;
  image: string;
  pharmacyCount?: number;
  pharmacistCount?: number;
  userType?: HelpTypes;
}

export interface IActiveUsers extends IGeneralPaginationResponse {
  data: Array<IAdminActiveUsersData>;
}

export interface IAvailableWorkshiftData {
  workshiftId: string;
  pharmacyId: string;
  name: string;
  startDate: string;
  endDate: string;
  city: string;
  hourlyRate: number;
  pharmacistHourlyRate: number;
  pharmacyHourlyRate: number;
  softwareName: string;
  logoId: string;
  isRequested: boolean;
  replacementType?: ProfessionalReplacementTypes;
  isAutoSyncRecord?: boolean;
  contractStartDate?: string;
  pharmacyWeekDayHourlyRate?: number;
  pharmacyWeekendHourlyRate?: number;
  contractEndDate?: string;
  weekDayHourlyRate?: number;
  weekendHourlyRate?: number;
  bookedShiftCount: number;
  longTermShiftContractId?: string;
  totalShiftCount: number;
}

export interface IAdminWorkshiftDetails {
  workshiftId: string;
  replacementType: ProfessionalReplacementTypes;
  pharmacyId: string;
  pharmacyName: string;
  banner: string;
  softwareName: string;
  softwareId: string;
  pharmacyCreatedDate: string;
  startDate: string;
  endDate: string;
  note: string;
  paidHourlyRate: number;
  city: string;
  address: string;
  province: string;
  region: string;
  latitude: string;
  longitude: string;
  pharmacyHourlyRate: number;
  logoId: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactMobile: string;
  contactPhoneExtension: string;
  travelAllowance: number;
  mealAllowance: number;
  emergencyFees?: number;
  accommodationAllowance?: number;
  pharmacistHourlyRate: number;
  languageId: string;
  notes: string;
  otherAllowance?: number;
  travelAllowanceAmount?: number;
  travelAllowanceRatePerKm?: number;
  accommodationAllowanceType?: string;
  travelAllowanceTypes?: Array<string>;
  longTermShiftContract: IUpdateLongTermShiftContract;
  shiftContractAttributes?: Array<ILongContractAttributes>;
  bookedShifts?: Array<ILongContractAttributes>;
  longTermShiftContractId?: string;
  bookedDates?: Array<string>;
  bookedWeekendCost?: number;
  bookedWeekDayCost?: number;
  pharmacyWeekDayHourlyRate?: number;
  pharmacyWeekendHourlyRate?: number;
}

export enum AdminLegendVariant {
  posted = 'posted',
  requested = 'requested',
  booked = 'booked',
  total = 'total',
}

export interface IAdminEditWorkshiftParams {
  workshiftId: string;
  startDate: string;
  endDate: string;
  paidHourlyRate?: number;
  travelAllowance?: number;
  mealAllowance?: number;
  accommodationAllowance?: number;
  emergencyFees?: number;
  note: string;
}

interface IGenericListData {
  bookingId: string;
  pharmacyId: string;
  workshiftId: string;
  logoId: string;
  pharmacistName: string;
  image: null | string;
  pharmacyName: string;
  startDate: string;
  endDate: string;
  workshiftHourlyRate: number;
  softwareName: string;
  pharmacyCity: string;
  pharmacistCity: string;
  pharmacistHourlyRate: number;
  pharmacistId: string;
  professionalId: string;
  professionalName?: string;
  replacementType?: ProfessionalReplacementTypes;
  externalPharmacistId?: string;
}
export interface IAdminRequestData {
  workshiftId: string;
  pharmacyId: string;
  pharmacyName: string;
  city: string;
  softwareName: string;
  workshiftHourlyRate: number;
  pharmacyHourlyRate: number;
  logoId: null | string;
  profilePictures: Array<string | null>;
  count: number;
  startDate: string;
  isAutoSyncRecord?: boolean;
  replacementType?: ProfessionalReplacementTypes;
  contractStartDate?: string;
  contractEndDate?: string;
  weekDayHourlyRate?: number;
  weekendHourlyRate?: number;
  bookedShiftCount?: number;
  totalShiftCount?: number;
  longTermShiftContractId?: string;
  pharmacistHourlyRate?: number;
  pharmacyWeekDayHourlyRate?: number;
  pharmacyWeekendHourlyRate?: number;
}

export interface IAdminRequestedPharmacistsData extends IGenericListData {
  workshiftId: string;
  pharmacistCity: string;
  professionalCity?: string;
  replacementType: ProfessionalReplacementTypes;
  pharmacyWeekDayHourlyRate?: number;
  pharmacyWeekendHourlyRate?: number;
  pharmacyHourlyRate: number;
  counterOfferId?: string;
  counterHourlyRate?: number;
  longTermShiftContract: ILongTermContractModel;
  bookedShiftCount?: number;
  totalShiftCount?: number;
  showOtherRequests?: boolean;
}

export interface IBookedPharmacistData extends IGenericListData {
  paidHourlyRate: number;
  pharmacyHourlyRate: number;
  isAutoSyncRecord?: boolean;
  longTermShiftContractId?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  weekDayHourlyRate?: number;
  weekendHourlyRate?: number;
  bookedShiftCount?: number;
  totalShiftCount?: number;
  contractCost?: number;
  professionalBookedCount?: number;
}

export interface IAdminRating {
  totalRating: number;
  count: number;
  pharmacistId: string;
}
export interface IAdminSoftware {
  software: IFilterSoftware;
  pharmacistId: string;
  softwareId: string;
  levelInternal: number;
  level: number;
}

export interface IAdminRequestedDetailsData {
  pharmacistId: string;
  professionalId?: string;
  firstName: string;
  lastName: string;
  languageId: string;
  language: IAllLanguages;
  pharmacistCapacity: IPharmacistCapacityDetails;
  phone: string;
  mobile: string;
  address: string;
  province: string;
  city: string;
  postalCode: string;
  experience: number;
  startDate: string;
  endDate: string;
  paidHourlyRate: number;
  email: string;
  bookingCost?: number;
  travelAllowance?: number;
  mealAllowance?: number;
  accommodationAllowance?: number;
  licenseNumber: string;
  emergencyFees?: number;
  softwares: Array<IAdminSoftware>;
  rating: {
    totalRating: number;
    count: number;
  } | null;
  profilePicture: string | null;

  travelAllowanceAmount?: number;
  travelAllowanceRatePerKm?: number;
  accommodationAllowanceType?: string;
  travelAllowanceTypes?: Array<string>;
  otherAllowance?: number;
  counterOfferId?: string;
  counterStartDate?: string;
  counterEndDate?: string;
  counterHourlyRate?: number;
  counterCost?: string;
  counterTravelAllowance?: number;
  counterMealAllowance?: number;
  counterEmergencyFees?: number;
  counterOtherAllowance?: number;
  counterAccommodationAllowance?: number;
  counterTravelAllowanceType?: Array<string>;
  counterTravelAllowanceAmount?: number;
  counterTravelAllowanceRatePerKm?: number;
  counterAccommodationAllowanceType?: string;
  longTermShiftContract?: ILongTermContractModel;
  replacementType?: ProfessionalReplacementTypes;
}
export interface IAdminIgnoreRequestParams {
  bookingId: string;
  note: string;
  sendEmailToPharmacist: boolean;
  isAutoSyncRecord?: boolean;
}

export interface IAdminIgnoreData {
  bookingId: string;
  pharmacyId: string;
  pharmacistId: string;
  pharmacyName: string;
  pharmacistName: string;
  startDate: string;
  endDate: string;
}

export interface IPharmaList {
  id: string;
  key: string | null;
  name: string;
}

export interface IAdminApproveDetailsData {
  bookingId: string;
  pharmacyId: string;
  pharmacistId: string;
  pharmaList: Array<IPharmaList>;
  professionalName?: string;
  pharmaId: string;
  pharmacyName: string;
  pharmacistName: string;
  startDate: string;
  endDate: string;
  travelAllowance?: number;
  mealAllowance?: number;
  accommodationAllowance?: number;
  pharmacyHourlyRate: number;
  pharmacistHourlyRate: number;
  emergencyFees?: number;

  travelAllowanceAmount?: number;
  travelAllowanceRatePerKm?: number;
  accommodationAllowanceType?: number;
  travelAllowanceTypes?: Array<string>;
  otherAllowance?: number;

  counterStartDate?: string;
  counterEndDate?: string;
  counterTravelAllowance?: number;
  counterMealAllowance?: number;
  counterAccommodationAllowance?: number;
  counterTravelAllowanceAmount?: number;
  counterTravelAllowanceRatePerKm?: number;
  counterAccommodationAllowanceType?: number;
  counterEmergencyFees?: number;
  counterTravelAllowanceType?: Array<string>;
  counterOfferId?: string;
  counterHourlyRate?: number;
  counterOtherAllowance?: number;
}

interface IAdminApproveBaseParams {
  pharmaId: string;
  notesForSalesOrder: string;
  pharmacistHourlyRate: number;
  pharmacyHourlyRate: number;
  travelAllowance: number;
  mealAllowance: number;
  accommodationAllowance: number;
  emergencyFees: number;
  sendEmailToPharmacist: boolean;
  sendEmailToPharmacy: boolean;
}
export interface IAdminApproveParams extends IAdminApproveBaseParams {
  bookingId: string;
}
export interface IAdminBookAvailabilityParams extends IAdminApproveBaseParams {
  workshiftId: string;
  availabilityId: string;
}

export interface IAdminSearchAPIParams {
  userTypes: HelpTypes[] | null;
  query: string;
  active?: boolean;
  isForDeletedUsers?: boolean;
  showAppointedPharmacies?: boolean;
}

export interface IMatchingAvailabilitiesData {
  availableTimeId: string;
  endDate: string;
  pharmacistId: string;
  pharmacistName: string;
  professionalName?: string;
  selectedDate: string;
  startDate: string;
  isRequested: boolean;
}

export interface IMatchingAvailabilities {
  city: string;
  endDate: string;
  logoId: string;
  matchingAvailabilities: Array<IMatchingAvailabilitiesData>;
  pharmacyName: string;
  startDate: string;
  typeReplacement: number;
  workShiftId: string;
}

export interface IAdminBookedPharmacistDetails {
  pharmacistId: string;
  pharmacyId: string;
  firstName: string;
  lastName: string;
  languageId: string;
  language: IAllLanguages;
  pharmacistCapacity: IPharmacistCapacityDetails;
  phone: string;
  mobile: string;
  address: string;
  province: string;
  city: string;
  postalCode: string;
  experience: number;
  licenseNumber: string;
  startDate: string;
  endDate: string;
  paidHourlyRate: number;
  email: string;
  createDate: string;
  bookingCost?: number;
  travelAllowance?: number;
  mealAllowance?: number;
  accommodationAllowance?: number;
  emergencyFees?: number;
  softwares: Array<IAdminSoftware>;
  rating?: IAdminRating;
  profilePicture: string;
  pharmacyName: string;
  reviewDetails: IReviewDetails;
  replacementType?: ProfessionalReplacementTypes;
  travelAllowanceAmount?: number;
  travelAllowanceRatePerKm?: number;
  accommodationAllowanceType?: string;
  otherAllowance?: number;
  travelAllowanceTypes?: Array<string>;
  longTermShiftContract?: ILongTermContractModel;
  bookedShiftCount?: number;
  contractCost?: number;
  professionalBookedCount?: number;
  longTermShiftContractId?: string;
}

export interface IRequestCancellationParams {
  bookingId: string;
  reason: string;
}

export interface IRequestCancellationData {
  startDate: string;
  endDate: string;
  pharmacyName: string;
  success: null | boolean;
  bookingId?: string;
  cancelationRequestId?: string;
  code?: string;
  message?: string;
}
export interface IPharmacyDetails {
  pharmacyId: string;
  email: string;
  languageId: string;
  timeFormat: TimeFormat;
  name: string;
  logoId: string;
  banner: string;
  pharmacyShortName: string | null;
  number: string | null;
  softwareId: string;
  selectedContactPreferences: Array<string>;
  sendReminder: boolean;
  code: string;
  administrator: string | null;
  weekVolume: string;
  weekendVolume: string;
  weekDayPharmacists: string;
  weekNightPharmacists: string;
  weekendDayPharmacists: string;
  weekendNightPharmacists: string;
  weekDayTechs: string;
  weekNightTechs: string;
  weekendDayTechs: string;
  weekendNightTechs: string;
  phone: string;
  emergencyPhone: string;
  postalCode: string;
  fax: string;
  address: string;
  city: string;
  province: string;
  region: string;
  regionId: string;
  contactEmail: string;
  primaryContactName: string;
  primaryContactPhone: string;
  primaryContactPhoneExt: string;
  primaryContactMobile: string | null;
  primaryContactEmail: string;
  secondaryContactName: string;
  secondaryContactPhone: string | null;
  secondaryContactPhoneExt: string | null;
  secondaryContactMobile: string | null;
  secondaryContactEmail: string | null;
  accountingContactName: string;
  accountingContactPhone: string;
  accountingContactPhoneExt: string;
  accountingContactMobile: string;
  accountingContactEmail: string;
  adminNotes: string;
  hourlyRate: number;
  useLSE: boolean;
  hourlyRateTech: number;
  pharmacyChainId: string;
  pharmacyHourlyRate: number;
  noCancellationPeriod: number;
  pharmaPlanPercentFees: number;
  cancellationPercentFees: number;
  latitude: null | number;
  longitude: null | number;
  communicationEmail: string;
  pharmacyWeekDayHourlyRate?: number;
  pharmacyWeekendHourlyRate?: number;
  professionalWeekDayHourlyRate?: number;
  professionalWeekendHourlyRate?: number;
}

export interface IPharmacyDetailModel {
  softwares: Array<IFilterSoftware>;
  contactPreferences: Array<IContactPreferences>;
  pharmacy: IPharmacyDetails;
  provinces: Array<IProvinces>;
  planName?: string | null;
}

export interface IUserDetailsBaseModel {
  email: string;
  preferdLanguage: string;
  timeFormat: number;
  firstName: string;
  lastName: string;
  licenseNumber: string;
  graduationYear: number;
  yearsOfExperience: number;
  incorporated: boolean;
  englishLevel: number | string;
  frenchLevel: number | string;
  otherLanguages: string;
  address: string;
  phone: string;
  mobile: string;
  city: string;
  postalCode: string;
  province: string;
  prefferedDistanceRange: number;
  remoteRegion: boolean;
  includeShiftContract: boolean;
  availabilityReminder: boolean;
  preferredReplacementDuration: string;
  preferredWorkingDays: string | string[];
  picture: string;
  hourlyRate: number;
  code: string;
  adminNotes: string;
  pictureName: string;
  rating: IAdminRating;
  resumeName: string;
  noCancellationPeriod: number;
  bookingStateExpiration: number;
  cancellationPercentFees: number;
  latitude: null | number;
  longitude: null | number;
  selectedContactPreferences: Array<string>;
  userType?: HelpTypes;
}

export interface IPharmacistDetailsBaseModel extends IUserDetailsBaseModel {
  pharmacistId: string;
  pharmacistCapacityId: string;
}

export interface IProfessionalDetailsBaseModel extends IUserDetailsBaseModel {
  professionalId: string;
  professionalCapacityId: string;
  accountNumber: string;
  institutionNumber: string;
  branchNumber: string;
  corporateCompanyName?: string | null;
  corporateCity?: string | null;
  corporatePhone?: string | null;
  corporateStreetAddress?: string | null;
  corporatePostalCode?: string | null;
  corporateProvince?: string | null;
  tps?: number | null;
  tvq?: number | null;
}

export interface IAdminProfessionalDetails
  extends IProfessionalDetailsBaseModel {
  selectedSoftwares: Array<IAdminSoftware>;
}

export interface IAdminPharmacistDetails extends IPharmacistDetailsBaseModel {
  selectedSoftwares: Array<IAdminSoftware>;
}

export interface ISoftwareSubmitParams {
  softwareId: string;
  expLevel: number;
}

export interface IPharmacistSubmission extends IPharmacistDetailsBaseModel {
  selectedSoftwares: Array<ISoftwareSubmitParams>;
  accountNumber?: string;
}

export interface IPharmacistDetailsModel {
  pharmacist: IAdminPharmacistDetails;
  provinces: Array<IProvinces>;
  prefferedReplacementDurations: Array<string>;
  pharmacistCapacities: Array<IPharmacistCapacity>;
  contactPreferences: Array<IContactPreferences>;
}

export interface IProfessionalDetailsModel {
  professional: IAdminProfessionalDetails;
  provinces: Array<IProvinces>;
  prefferedReplacementDurations: Array<string>;
  professionalCapacities: Array<IProfessionalCapacity>;
  contactPreferences: Array<IContactPreferences>;
}

export interface IAdminSearchResponse {
  id: string;
  userId: string;
  userName: string;
  email: string;
}

export interface IExpressBookingAPIParams {
  bookingId?: string;
  workshiftId?: string;
  pharmacyId: string;
  pharmacistId?: string;
  startDate: string;
  endDate: string;
  notes: string;
  notesForSalesOrder: string;
  pharmacistHourlyRate: number;
  pharmacyHourlyRate: number;
  travelAllowance?: number;
  mealAllowance?: number;
  accommodationAllowance?: number;
  emergencyFees?: number;
  sendEmailToPharmacist: boolean;
  sendEmailToPharmacy: boolean;
}

export interface IValidateExpressBookingAPIParams {
  pharmacistId?: string;
  pharmacyId: string;
}

export interface IAdminPendingActivationAPIParams extends GenericPageWithSort {
  startDate?: string | null;
  endDate?: string | null;
  userKey?: string | null;
  userType?: HelpTypes | null;
  ownerId?: string;
}

export interface IAdminTakeOwnershipAPIParams {
  userType: HelpTypes;
  id: string;
}

export interface IAdminPendingActivationsData {
  userId: string;
  personnelId: string;
  ownerId: null | string;
  email: string | null;
  userName: string;
  contactName: null | string;
  createdDate: string;
  deletedDate?: string;
  userType?: HelpTypes;
  ownerName: string | null;
  enableActions: boolean;
  logoId?: string;
}

export interface IAdminPendingActivationsResponse
  extends IGeneralPaginationResponse {
  data: Array<IAdminPendingActivationsData>;
}

export interface ISubmitPharmacistDetails extends IPharmacyDetails {
  pharmacyId: string;
}

export interface IAdminCreateSoftwareApiParams {
  name: string;
  description: string;
  published: boolean;
}

export interface IAdminUpdateSoftwareApiParams
  extends IAdminCreateSoftwareApiParams {
  softwareId: string;
}

export interface IAdminCreatePharmacistCapacityApiParams {
  from: number;
  to: number;
  published: boolean;
}

export interface IAdminUpdatePharmacistCapacityApiParams
  extends IAdminCreatePharmacistCapacityApiParams {
  pharmacistCapacityId: string;
}

interface IAdminPharmacistCapacity {
  pharmacistCapacityId: string;
  from: number;
  to: number;
  published: boolean;
}

export interface IAdminPharmacistCapacityList extends IAdminPharmacistCapacity {
  inUse: boolean;
}

export interface IAdminPharmacistCapacityItem extends IAdminPharmacistCapacity {
  createDate: string;
  updateDate: string | null;
}

export interface IAdminContactPreferences {
  contactPreferenceId: string;
  name: string;
  published: boolean;
  inUse: boolean;
}

export interface IAdminConfiguration {
  enableChat: boolean;
  pharmaPlanConfigurationId: string;
  ratePharmacistDays: number;
  ratePharmacyDays?: number;
  minHourWorkshift: number;
  pharmacyRate: number;
  pharmacistRate: number;
  representativeName: string;
  representativeEmail: string;
  representativePhone: string;
  representativePhoneExt: string;
  salesForceUserName: string;
  salesForcePassword: string;
  salesForceSecurityToken: string;
  salesForceURL: string;
  envoiceBCCAdmin: boolean;
  salesForceActive: boolean;
  showBookingDetails: boolean;
  technicianRate: number;
  noCancellationPeriod: number;
  bookingStateExpiration: number;
  pharmaPlanPercentFees: number;
  cancellationPercentFees: number;
  freeCancellationPeriod: number;
  reminder1After: number;
  reminder2Before: number;
  basicPackagePrice?: number;
  pharmacyReminderBefore?: number;
  unlimitedPackagePrice?: number;

  tpsPercentage?: number;
  tvqPercentage?: number;
  taxOnTax?: boolean;
  companyName?: string | null;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  phoneNumber?: string | null;
  pharmacyWeekDayHourlyRate?: number;
  pharmacyWeekendHourlyRate?: number;
  professionalWeekDayHourlyRate?: number;
  professionalWeekendHourlyRate?: number;
  tpsNumber?: string;
  tvqNumber?: string;
}

export interface ISalesforceAdmin {
  adminPharmaPlanId: string;
  name: string;
  email: string;
  phone: null | string;
  received: boolean;
}
export interface IAdminCreatePharmacyChainApiParams {
  name: string;
  published: boolean;
  form: any;
}

export interface IAdminUpdatePharmacyChainApiParams
  extends IAdminCreatePharmacyChainApiParams {
  pharmacyChainId: string;
  isLogoChanged: boolean;
}

export interface IAdminPharmacyChainResponse {
  pharmacyChainId: string;
  logoId: string;
  name: string;
  published: boolean;
  logoImage: string | null;
  inUse: boolean;
  imageName: string;
}

export interface IPharmacistRegionList {
  allRegions: Array<IRegions>;
  selectedRegions: Array<string>;
}

export interface IUpdatePharmacistRegions {
  pharmacistId: string;
  regions: Array<string>;
}

export interface IPendingCancellationApiParmas extends GenericPageWithSort {
  startDate?: string | null;
  endDate?: string | null;
  code?: string;
  pharmacyText?: string;
  pharmacistText?: string;
}

export interface IPendingCancellationData {
  cancellationRequestId: string;
  cancellationId: string | null;
  cancellationRejectionDate: string | null;
  bookingId: string;
  requestedBy: string;
  cancellationRequestDate: string;
  selectedDate: string;
  cancellationDate: string | null;
  requestedByName: string;
  status: PendingCancellationStatus;
  requestCode: string;
  rejectionReason: string | null;
  cancellationCode: string | null;
  requsetCancellationReason: string;
  cancellationReason: string | null;
  pharmacyId: string;
  pharmacyName: string;
  isPharmacyDeleted: boolean;
  startDate: string;
  endDate: string;
  pharmacistId: string;
  pharmacistName: string;
  isPharmacistDeleted: boolean;
  multipleBookingCancellationId?: string;
  professionalName?: string;
  professionalId?: string;
}

export interface IPendingCancellationsResponse
  extends IGeneralPaginationResponse {
  data: Array<IPendingCancellationData>;
}
export interface IPharmacistResume {
  fileName: string;
  contentType: string;
  resume: string;
}

export interface IAdminPendingCancellationBookingList {
  bookingId: string;
  selectedDate: string;
  shiftCode: string;
  availabilityCode: string;
}

export interface IPendingCancellationCodeDetails {
  cancellationRequestId: string;
  cancellationId: string;
  bookingId: string;
  requestedBy: string;
  approvedBy: string | null;
  status: PendingCancellationStatus;
  requestType: string;
  requestReason: string;
  cancellationReason: string | null;
  approvedByName: string | null;
  selectedDate: string;
  cancellationDate: string;
  requestCode: string;
  confirmationCode: string | null;
  pharmacyId: string;
  pharmacyName: string;
  pharmacistId: string;
  pharmacistName: string;
  workshiftCode: string;
  rejectedBy: string | null;
  rejectionReason: string | null;
  bookings?: Array<IAdminPendingCancellationBookingList>;
  rejectedByName: string | null;
  cancellationRequestDate: string;
  cancellationRejectionDate: string | null;
  availabilityCode: string;
}

export interface IAdminApprovePendingCancellationApiParams {
  cancellationId: string;
  notes: string;
  sendEmailToPharmacist: boolean;
  sendEmailToPharmacy: boolean;
}

export interface IAdminRejectPendingCancellationApiParams {
  cancellationId: string;
  notes: string;
}

export interface IAdminActivePharmacistListApiParams
  extends GenericPageWithSort {
  page: number;
  userText?: string;
  userType?: HelpTypes;
}

export interface IAdminCreateAvailabilityApiParams {
  pharmacistId: string;
  model: IAvailabilityModel;
}

export interface IAdminPharmacistListData {
  pharmacistId: string;
  professionalId?: string;
  code: string;
  name: string;
  email: string;
  mobile: string;
  city: string;
}

export interface IAdminPharmacistListApiResponse
  extends IGeneralPaginationResponse {
  data: Array<IAdminPharmacistListData>;
}
export interface IPendingCancellationCodeDetails {
  cancellationRequestId: string;
  cancellationId: string;
  bookingId: string;
  requestedBy: string;
  approvedBy: string | null;
  requestType: string;
  requestReason: string;
  cancellationReason: string | null;
  approvedByName: string | null;
  selectedDate: string;
  cancellationDate: string;
  requestCode: string;
  confirmationCode: string | null;
  pharmacyId: string;
  pharmacyName: string;
  pharmacistId: string;
  pharmacistName: string;
  workshiftCode: string;
  availabilityCode: string;
}

export interface ICancelledBookingDetails
  extends IPendingCancellationCodeDetails {
  rejectedBy: string | null;
  rejectionReason: string | null;
  rejectedByName: string | null;
  cancellationRequestDate: string;
  cancellationRejectionDate: string | null;
}
export interface IAdminNoteApiBase {
  notes: string;
}

export interface IAdminCreateNoteApiParams extends IAdminNoteApiBase {
  pharmacistId: string;
}

export interface IAdminCreatePharmacyNoteApiParams extends IAdminNoteApiBase {
  pharmacyId: string;
}

export interface IAdminUpdateNoteApiParams extends IAdminNoteApiBase {
  adminPharmacistNoteId: string;
}

export interface IAdminUpdatePharmacyNoteApiParams extends IAdminNoteApiBase {
  adminPharmacyNoteId: string;
}

export interface IAdminNotesResponseBase {
  note: string;
  createDate: string;
  updateDate: string | null;
}

export interface IAdminNotesResponse extends IAdminNotesResponseBase {
  adminPharmacistNoteId: string;
  pharmacistId: string;
}

export interface IAdminPharmacyNotesResponse extends IAdminNotesResponseBase {
  adminPharmacyNoteId: string;
  pharmacyId: string;
}

export interface IAdminPharmacistBookingsAPIParams {
  pharmacistId: string;
  source?: Agencies;
  paging: {
    page: number;
  };
}

export interface IAdminPharmacistBookingData {
  bookingId: string;
  pharmacistId: string;
  pharmacyId: string;
  logoId: string;
  name: string;
  startDate: string;
  endDate: string;
  city: string;
  isAutoSyncRecord?: boolean;
  paidHourlyRate: number;
  longTermShiftContractId?: string;
  weekDayPaidHourlyRate?: number;
  weekendPaidHourlyRate?: number;
}

export interface IAdminPharmacistBookingResponse
  extends IGeneralPaginationResponse {
  data: Array<IAdminPharmacistBookingData>;
}

export interface IAdminPharmacistBooking {
  bookingId: string;
  startDate: string;
  endDate: string;
  paidHourlyRate: number;
  cost: number;
  travelAllowance: number;
  mealAllowance: number;
  accommodationCosts: number;
  emergencyCosts: number;
  pharmaPlanPercentFees: string;
  logoId: string;
  languageId: string;
  latitude: string;
  longitude: string;
  pharmacyName: string;
  pharmacyChainName: string;
  number: string;
  softwareName: string;
  languageCode: string;
  createDate: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  city: string;
  province: string;
  region: string;
  postalCode: string;
  pharmacyHourlyRate: number;
  pharmacistHourlyRate: number;
  professionalHourlyRate?: number;
  longTermShiftContract?: ILongTermContractModel;
  pharmacyWeekDayHourlyRate?: number;
  pharmacyWeekendHourlyRate?: number;
  totalProfessionalCost?: number;
  totalPharmacyCost?: number;
  totalWeekHours?: string;
  totalWeekendHours?: string;
}

export interface IAdminDeletePharmacistAvailabilityAPIParams {
  availabilityIds: Array<string>;
  notes: string;
}

export interface IAdminShowAvailabilitiesData {
  availableTimeId: string;
  pharmacistId: string;
  code: string;
  startDate: string;
  endDate: string;
}

export interface IAdminShowAvailabilitiesResponse
  extends IGeneralPaginationResponse {
  data: Array<IAdminShowAvailabilitiesData>;
}

export interface IMatchingWorkshiftAvailabilityData {
  workShiftId: string;
  pharmacyId: string;
  logoId: string;
  pharmacyName: string;
  selectedDate: string;
  startDate: string;
  booked: boolean;
  endDate: string;
  isRequested: boolean;
}

export interface IMatchingWorkshiftsResponse {
  availableTimeId: string;
  pharmacistName: string;
  pharmacistId: string;
  startDate: string;
  endDate: string;
  city: string;
  mobile: string;
  matchingAvailabilities: Array<IMatchingWorkshiftAvailabilityData>;
}

export interface IDeactivePharmacistApiParams {
  pharmacistId: string;
  reason: string;
}
export interface IAdminPharmacistPictures {
  pharmacistId: string;
  image: string;
}

export interface IPendingActivationsCount {
  pharmacistCount?: number;
  pharmacyCount: number;
  technicianCount: number;
  studentCount: number;
}

export interface IOwnersList {
  userId: string;
  userName: string;
  email: string;
}

export interface IAdminListOfPharmaciesApiParams {
  pharmacistId: string;
  source?: Agencies;
  paging: {
    page: number;
  };
}

export interface IAdminPharmacistHistoryBaseApiParams {
  pharmacistId: string;
  pharmacyId: string;
  isAutoSyncRecord?: boolean;
}

export interface IAdminPharmacistHistoryApiParams
  extends IAdminPharmacistHistoryBaseApiParams {
  paging: {
    page: number;
  };
}

export interface IAdminListOfPharmaciesData {
  pharmacyId: string;
  logoId: string;
  location: string;
  city: string;
  favourite: boolean;
  pharmacyName: string;
  totalHours: string;
  totalWorkshift: string;
  isAutoSyncRecord?: boolean;
}

export interface IAdminListOfPharmaciesResponse
  extends IGeneralPaginationResponse {
  data: Array<IAdminListOfPharmaciesData>;
}

export interface IAdminPharmacistHistoryData {
  code: string;
  selectedDate: string;
  startDate: string;
  endDate: string;
  bookingId: string;
}

export interface IAdminPharmacistNotesData
  extends IAdminPharmacistHistoryApiParams {
  pharmacistPharmacyNoteId: string;
  note: string;
  createDate: string;
  updateDate: null | string;
}

export interface IAdminPharmacistHistoryResponse
  extends IAdminPharmacistHistoryBaseApiParams {
  data: Array<IAdminPharmacistHistoryData>;
  totalCount: number;
}

export interface IDeactivatedPharmacistListApiParams {
  page: number;
  userText?: string;
}

export interface IActivatePharmacistApiParams {
  pharmacistId: string;
  reason: string;
}

export interface IAdminPharmacistListBaseData {
  pharmacistId: string;
  code: string;
  name: string;
  email: string;
  mobile: string;
  city: string;
  lastActivityDate: string | null;
  deletedDate: string;
  userId: string;
  hourlyRate: number;
  licenseNumber: string;
  watchlistCount: number;
}

export interface IAdminPharmacistListResponse
  extends IGeneralPaginationResponse {
  data: Array<IAdminPharmacistListBaseData>;
}
export interface IAdminPharmacyListBaseParams {
  page: number;
  userText?: string;
  softwareId?: string;
  banners?: string;
  sortBy?: string;
  desc?: boolean;
  contactName?: string;
  city?: string;
}

export interface IAdminPharmacyListBaseData {
  pharmacyId: string;
  logoId: string;
  name: string;
  email: string;
  mobile: string | null;
  city: string;
  contactName: string;
  pharmacyChainId: string;
  softwareId: string;
  lastActivityDate: null | string;
  deletedDate: null | string;
  bannerNumber: string;
  userId: string;
  hourlyRate: number;
  pharmacyHourlyRate: number;
  appointedPharmacyId: string | null;
  planName?: string | null;
}

export interface IActivePharmacyList extends IGeneralPaginationResponse {
  data: Array<IAdminPharmacyListBaseData>;
}

export interface IAdminCreateWorkshiftModel {
  startDate: string;
  endDate: string;
  recurrenceType: number;
  note: string;
  weekNumber: number | string;
  day: number;
  weekDays: null | number | string;
  month: number;
  emergencyFees?: number;
  mealAllowance?: number;
  travelAllowance?: number;
  paidHourlyRate?: number;
  accommodationAllowance?: number;
  pharmacistHourlyRate?: number;
}

export interface IAdminCreateWorkshiftParams {
  pharmacyId: string;
  model: IAdminCreateWorkshiftModel;
}

export interface IPublishedSoftwareList {
  softwareId: string;
  languageId: string;
  name: string;
  description: string;
}

export interface IAdminListOfPharmacistsApiParams
  extends GenericPaginationAPIParams {
  pharmacyId: string;
}

export interface IAdminListOfPharmacistsData {
  pharmacistId: string;
  role: number;
  totalReplacements: number;
  totalHours: number;
  favourite: boolean;
  isIncompatible: boolean;
  name: string;
  city: string;
}

export interface IAdminListOfPharmacistsResponse
  extends IGeneralPaginationResponse {
  data: Array<IAdminListOfPharmacistsData>;
}

export interface IAdminIncompatibleMatchApiParams
  extends GenericPaginationAPIParams {
  pharmacyId: string;
  userType?: HelpTypes;
}

export interface IAdminIncompatibleMatchData {
  incompatibleMatchId: string;
  pharmacistId: string;
  name: string;
  email: string;
  mobile: string;
  phone: string;
  licenseNumber: string;
  city: string;
  address: string;
}

export interface IAdminIncompatibleMatchResponse
  extends IGeneralPaginationResponse {
  data: Array<IAdminIncompatibleMatchData>;
}

export interface IAdminUpdateIncompatibleMatchApiParams {
  pharmacyId: string;
  note: string;
  pharmacistIds: Array<string>;
  userType: HelpTypes;
}

export interface IAdminPharmacyWorkshiftsListAPIParams
  extends GenericPaginationModelAPIParams {
  pharmacyId: string;
  past: boolean;
  status: PharmacyFilterWorkshifts | string;
}

export interface IAdminPharmacyWorkshiftsListData {
  id: string;
  workShiftId: string;
  pharmacyId: string;
  selectedDate: string;
  startDate: string;
  endDate: string;
  totalHours: number;
  totalMinutes: number;
  note: null | string;
  type: ICalendarEventType;
  status: WorkshiftStatus | WorkshiftReportStatus;
  bookingId: null | string;
  booked: boolean;
  code: string;
  pharmacistName: string;
  recurrenceId: null | string;
  replacementType: number;
  confirmationDate: null | string;
  gracePeriodInHours: number;
  bookingState: number;
  canReportIssue: boolean;
  pharmacistId: string;
  pharmacyHourlyRate: number;
  paidHourlyRate: number;
  professionalName?: string;
  externalPharmacistId?: string;
  longTermShiftContractId?: string;
  bookedShiftCount?: number;
  totalShiftCount?: number;
  weekendPaidHourlyRate?: number;
  weekDayPaidHourlyRate?: number;
  pharmacyWeekendHourlyRate?: number;
  pharmacyWeekDayHourlyRate?: number;
}

export interface IAdminPharmacyWorkshiftsList
  extends IGeneralPaginationResponse {
  data: Array<IAdminPharmacyWorkshiftsListData>;
}

export interface IAdminDeleteWorkshift {
  workshiftIds: Array<string>;
  notes: string;
}

export interface IAdminDeleteSingleWorkshift {
  workshiftId: string;
  notes: string;
}

export interface IAdminDisapprove {
  id: string;
  reason: string;
}

export interface IAdminIncompatibleMatchesSearch {
  pharmacyId: string;
  pharmacies: string;
  pharmacySelected: string;
  userType: HelpTypes;
}

export interface IAdminReportsAPIParams extends GenericPageWithSort {
  type: ICalendarEventType;
  startDate?: string | null;
  endDate?: string | null;
  replacementType?: number;
  pharmacistId?: string;
  availabilityStatus?: number;
  source?: Agencies;
}

export interface IAdminReportsData {
  bookingId: string;
  code: string;
  logoId: string;
  location: string;
  pharmacistHourlyRate: number;
  pharmacyHourlyRate: number;
  startDate: string;
  endDate: string;
  status: WorkshiftStatus;
  selectedDate: string;
  pharmacyName: string;
  replacementType: number;
  pharmacistName: string;
  professionalName?: string;
  city: string;
  notes: string;
  gracePeriodInHours: number;
  availabilityStatus: boolean;
  workshiftId: string;
  pharmacyId: string;
  pharmacistId: string;
  hours: number;
  professionalId: string;
  availableTimeId: string;
  externalPharmacistId?: string;
  longTermShiftContractId?: string;
  weekendPaidHourlyRate?: number;
  weekDayPaidHourlyRate?: number;
  pharmacyWeekDayPaidHourlyRate?: number;
  pharmacyWeekendPaidHourlyRate?: number;
  bookedShiftCount?: number;
  totalShiftCount?: number;
  paymentId?: string;
}

export interface IAdminReportsResponse {
  page: number;
  totalCount: number;
  type: string;
  data: Array<IAdminReportsData>;
}

export interface IGenericAdminReportsFilter {
  id: string;
  key: string;
  name: string;
}

export interface IAdminReportsFilter {
  replacementTypes: Array<IGenericAdminReportsFilter>;
  availabilityStatuses: Array<IGenericAdminReportsFilter>;
  sortBy: Array<IGenericAdminReportsFilter>;
}

export interface IAdminReportsPDF {
  pdfContent: string;
}

export interface IAdminListingApiParams extends GenericPageWithSort {
  userType?: AdminTypes | '';
  userText?: string;
}

export interface IApproveAdminsApiParams {
  userId: string;
  reason: string;
}

export interface IAdminListingResponseData {
  userId: string;
  email: string;
  userName: string;
  userType: AdminTypes;
  lastActivityDate: null | string;
  lastLoginDate: null | string;
}

export interface IAdminListingResponse extends IGeneralPaginationResponse {
  data: Array<IAdminListingResponseData>;
}
export interface IAdminDeletePharmacyChainsAPIParams {
  pharmacyChainId: string;
  reason: string;
}

export interface IAdminDeletePharmacistCapacitiesAPIParams {
  pharmacistCapacityId?: string;
  professionalCapacityId?: string;
  reason: string;
}

export interface IAdminDeleteSoftwaresAPIParams {
  softwareId: string;
  reason: string;
}

export interface ICreateAdminParams {
  email: string;
  userType: AdminTypes;
  username: string;
  languageId: string;
  permissions: Array<string>;
  phone: number;
  supportAgentName: string;
}

export interface IUpdateAdminParams extends ICreateAdminParams {
  userId: string;
}

export interface IPermissionData {
  permissionId: string;
  type: PermissionsOfAdmin;
  description: string;
}

export interface IPermissionsResponse {
  permissions: Array<IPermissionData>;
}

export interface IAdminDetailsResponse {
  userId: string;
  userName: string;
  email: string;
  userType: AdminTypes;
  languageId: string;
  permissions: Array<{ permissionId: string }>;
  phone: string;
  supportAgentName: string;
}
export interface IAdminCreateContactPreferenceApiParams {
  name: string;
  published: boolean;
}

export interface IAdminUpdateContactPreferenceApiParams
  extends IAdminCreateContactPreferenceApiParams {
  contactPreferenceId: string;
}

export interface IAdminDeleteContactPreferenceApiParams {
  contactPreferenceId: string;
  reason: string;
}

export interface IAdminPharmacistDetailsSearchApiParams {
  softwareIds?: Array<string>;
  capacityIds?: Array<string>;
  userType?: HelpTypes;
  paging: {
    page: number;
  };
}

export interface IAdminPharmacistDetailsSearchData {
  pharmacistId: string;
  firstName: string;
  lastName: string;
  name: string;
  phone: string;
  mobile: string;
  city: string;
  licenseNumber: string;
  capacityId: string;
  capacityTo: number;
  capacityFrom: number;
  softwareId: string;
  software: string;
  expLevel: number;
  email: string;
}

export interface IAdminPharmacistDetailsSearchResponse
  extends IGeneralPaginationResponse {
  data: Array<IAdminPharmacistDetailsSearchData>;
}

export interface IAdminPharmacistDetailsSearchFilters {
  pharmacistCapacities: Array<IPharmacistCapacityDetails>;
  professionalCapacities: Array<IProfessionalCapacityDetails>;
}

export interface IAdminPharmaciesInfo {
  hourlyRate: number;
  pharmacyHourlyRate: number;
  travelAllowanceTypes?: Array<IBackendDropdown>;
  accommodationAllowanceTypes?: Array<IBackendDropdown>;
  planName?: string | null;
  pharmacyWeekDayHourlyRate?: number;
  pharmacyWeekendHourlyRate?: number;
  professionalWeekDayHourlyRate?: number;
  professionalWeekendHourlyRate?: number;
}

export interface IRatingDetailsLevelBase {
  level: number;
  percentage: string;
}
export interface IRatingDetailsLevel extends IRatingDetailsLevelBase {
  pharmacistRatingItemId: string;
}
export interface IProfessionalRatingDetailsLevel
  extends IRatingDetailsLevelBase {
  professionalRatingItemId: string;
}

export interface IPharmacyRatingDetailsLevel extends IRatingDetailsLevelBase {
  pharmacyRatingItemId: string;
}

export interface IRatingDetailsResponse {
  pharmacistRatingId: string;
  pharmacyName: string;
  rating: string;
  note: string;
  ratingPercentage: string;
  createDate: string;
  pharmacyCode: string;
  logoId: string;
  levels: Array<IRatingDetailsLevel>;
}

export interface IProfessionalAnswers {
  professionalRatingQuestionId: string;
  answer: string;
}

export interface IPharmacyAnswers {
  pharmacyRatingQuestionId: string;
  answer: string;
}

export interface IProfessionalRatingDetailsResponse {
  professionalRatingId: string;
  pharmacyName: string;
  rating: string;
  note: string;
  ratingPercentage: string;
  createDate: string;
  pharmacyCode: string;
  logoId: string;
  levels: Array<IProfessionalRatingDetailsLevel>;
  answers: Array<IProfessionalAnswers>;
}

export interface IPharmacyRatingDetailsResponse {
  pharmacyRatingId: string;
  pharmacistName: string;
  rating: string;
  note: string;
  ratingPercentage: string;
  createDate: string;
  pharmacyCode: string;
  logoId: string;
  levels: Array<IPharmacyRatingDetailsLevel>;
  answers: Array<IPharmacyAnswers>;
}

export interface IAdminSettings {
  defaultPharmacistRate: number;
  permissions: Array<{ permissionId: string }>;
}
export interface IAdminBroadcastUserGroupsPharmacistApiParams
  extends GenericPageWithSort {
  userType: HelpTypes | '';
  city?: string;
  userText?: string;
}

export interface IAdminBroadcastUserGroupsPharmacyApiParams
  extends GenericPageWithSort {
  userType: HelpTypes | '';
  city?: string;
  userText?: string;
  softwareIds?: Array<string>;
  banners?: Array<string>;
  weekVolume?: IVolumes;
  weekendVolume?: IVolumes;
}

export interface IAdminBroadcastUserGroupApiData {
  userId: string;
  id: string;
  softwareId: null | string;
  logoId: null | string;
  pharmacyChainId: null | string;
  email: string;
  userName: string;
  userType: HelpTypes;
  mobile: string;
  city: string;
  softwareName: null;
  pharmacistSoftwareIds: null | Array<string>;
  communicationEmail: string;
}

export interface IAdminBroadcastUserGroupResponse
  extends IGeneralPaginationResponse {
  data: Array<IAdminBroadcastUserGroupApiData>;
}

export interface IAdminCreateBroadcastGroupApiParams {
  groupName: string;
  userIds: Array<string>;
}

export interface IAdminUpdateBroadcastGroupApiParams {
  groupName: string;
  groupId: string;
  userIds: Array<string>;
}

export interface IAdminBroadcastGroupListApiParams
  extends GenericPaginationModelAPIParams {
  groupName?: string;
}

export interface IAdminBroadcastGroupListData {
  broadcastGroupId: string;
  name: string;
  userId: string;
  userName: string;
  createDate: string;
  updateDate: null | string;
  broadcastGroupUsers: Array<IAdminBroadcastUserGroupApiData>;
}

export interface IAdminBroadcastGroupListResponse
  extends IGeneralPaginationResponse {
  data: Array<IAdminBroadcastGroupListData>;
}

export interface IAdminBroadcastGroupFilter {
  weekVolumes: Array<IVolumes>;
  weekendVolumes: Array<IVolumes>;
}

export interface IBroadcastNotificationsApiParams extends GenericPageWithSort {
  type?: BroadcastNotificationType;
  startDate?: string | null;
  endDate?: string | null;
  groupIds?: Array<string>;
}

export interface IBroadcastNotificationsData {
  broadcastMessageId: string;
  type: BroadcastNotificationType;
  sentDate: string;
  subject: string;
  message: string;
  footer: string;
  broadcastGroups: Array<IAdminBroadcastGroupListData>;
}

export interface IBroadcastNotificationApiResponse
  extends IGeneralPaginationResponse {
  data: Array<IBroadcastNotificationsData>;
}

export interface IAdminSendBroadcastApiParams {
  type: BroadcastNotificationType;
  subject: string;
  message: string;
  groupIds: Array<string>;
  footer: string;
}

export interface IAdminLogsGenericParams extends GenericPageWithSort {
  startDate: string | null;
  endDate: string | null;
  performerText: string;
  userText: string;
  action: LogActionTypes | null | IActionTypeFormik;
  module?: null | ActivityLogModuleTypes;
  pharmacistText?: string;
  pharmacyId?: string;
  pharmacistId?: string;
}

export interface IActionTypeFormik {
  key: LogActionTypes;
  module: ActivityLogModuleTypes;
}

export interface IAdminLogsGenericData {
  code: string;
  startDate: string;
  endDate: string;
  selectedDate: string;
  performerUserId: string;
  performerName: string;
  userPersonnelId: string;
  userName: string;
  pharmacyHourlyRate: number;
  pharmacistHourlyRate: number;
  action: LogActionTypes | null | IActionTypeFormik;
  actionDate: string;
  notes: string;
  isBooked: boolean;
  performerUserType: PerformerTypes;
  userType: PerformerTypes;
  performerPersonnelId: string;
  logId: string;
  isUserActive: boolean;
  isPerformerActive: boolean;
  isPharmacistActive: boolean;
  pharmacistName: string;
  pharmacistId: string;
}

export interface IAdminLogsGenericResponse {
  totalCount: number;
  itemPerPage: number;
  page: number;
  data: Array<IAdminLogsGenericData>;
}

export interface IAdminLogsFilterKeys {
  key: string;
  value: string;
  modules: Array<IAdminLogsFilterKeys>;
}

export interface IGenericMultilevelDropdown extends IAdminLogsFilterKeys {
  modules: Array<IAdminLogsFilterKeys>;
}

export interface IAdminLogsFilters {
  workshiftActions: Array<IAdminLogsFilterKeys>;
  availabilityActions: Array<IAdminLogsFilterKeys>;
  bookingActions: Array<IAdminLogsFilterKeys>;
  activityActions: Array<IGenericMultilevelDropdown>;
}

export interface ILogsUserData {
  userId: string;
  personnelId: string;
  ownerId: null | string;
  email: string;
  userName: string;
  contactName: null | string;
  createdDate: null | string;
  ownerName: null | string;
  approved: boolean;
  userType: PerformerTypes;
  isUserActive: boolean;
}

export interface IActivityLogsData {
  applicationActivityId: string;
  performerUserId: string;
  performerUserType: PerformerTypes;
  performerName: string;
  performerEmail: string;
  performerPersonnelId: string;
  action: ActivityLogActionTypes;
  module: ActivityLogModuleTypes;
  actionDate: string;
  notes: string;
  users: Array<ILogsUserData>;
  isUserActive: boolean;
  affectedCount: number;
  actionText: string;
  showCountOnly: boolean;
}

export interface IActivityLogsResponse {
  totalCount: number;
  itemPerPage: number;
  page: number;
  data: Array<IActivityLogsData>;
}

export interface IDeleteBroadcastGroupApiParams {
  groupId: string;
  reason: string;
}

export interface IDeleteBroadcastNotificationApiParams {
  broadcastNotificationId: string;
  reason: string;
}

export interface IAdminInfo {
  userId: string;
  id: string;
  userName: string;
  code: Language;
  languageId: string;
  phone: string;
  email: string;
  supportAgentName?: string;
}

export interface IUpdateAdminInfo {
  languageId: string;
  phone: string;
  supportAgentName?: string;
  username: string;
}

export interface IAdminPicture {
  contentType: string;
  image: string;
  name?: string;
}

export interface IPharmacyAdminsListingAPIResponse {
  pharmacyCompanyId: string;
  name: string;
  email: string;
  mobile: string;
  active: boolean;
  pharmacies: Array<IPharmacyAdminPharmacies>;
}

export interface IPharmacyAdminDetails {
  pharmacyCompany: {
    user: {
      language: IAllLanguages;
      userId: string;
      email: string;
      userName: string;
      personnelId: string;
      languageId: string;
      culture: string;
      createDate: string;
      timeFormat: TimeFormat;
    };
    pharmacyCompanyId: string;
    appointedPharmacyId: string | null;
    userId: string;
    name: string;
    description: string;
    phone: string;
    mobile: string;
    province: string;
    createDate: string;
    active: boolean;
  };
  pharmacies: Array<IPharmacyAdminPharmacies>;
}

export interface ICreatePharmacyAdmin {
  email: string;
  languageId: string;
  name: string;
  description: string;
  phone: string;
  mobile: string;
  province: string;
  pharmacyIds: Array<string>;
  address: string;
  city: string;
  postalCode: string;
}

export interface IUpdatePharmacyAdmin extends ICreatePharmacyAdmin {
  pharmacyCompanyId: string;
}

export interface IAppointPharmacyParams {
  pharmacyCompany: Omit<ICreatePharmacyAdmin, 'pharmacyIds'>;
  pharmacyId: string;
  pharmacyIds: Array<string>;
}

export interface IAdminPharmacyAdminProfile {
  provinces: Array<IProvinces>;
}

export interface IGetPharmaciesForPharmacyAdminParams
  extends GenericPageWithSort {
  userText?: string;
  banners?: Array<string>;
  softwareId?: string;
  city?: string;
  contactName?: string;
}

export interface IPharmaciesInPharmacyAdmin {
  pharmacyId: string;
  userId: string;
  logoId: string;
  name: string;
  email: string;
  mobile: string;
  city: string;
  bannerNumber: null;
  contactName: string;
  hourlyRate: number;
  pharmacyHourlyRate: number;
  pharmacyChainId: string;
  softwareId: string;
  lastActivityDate: null;
  deletedDate: null;
  weekVolume: null;
  weekendVolume: null;
}

export interface IPharmaciesInPharmacyAdminResponse
  extends IGeneralPaginationResponse {
  data: Array<IPharmaciesInPharmacyAdmin>;
}

export interface IDeletePharmacyAdminParams {
  pharmacyCompanyId: string;
  reason: string;
}

export interface IBroadcastGroupParams extends GenericPaginationModelAPIParams {
  groupId: string;
}

export interface IBroadcastGroupModel {
  broadcastGroupId: string;
  name: string;
  userId: string;
  userName: string;
  createDate: string;
  updateDate: string | null;
  groupUsersCount: number;
}

export interface IBroadcastGroupData {
  userId: string;
  id: string;
  softwareId: string | null;
  logoId: string;
  pharmacyChainId: string | null;
  email: string;
  userName: string;
  userType: PerformerTypes;
  mobile: string | null;
  city: string | null;
  softwareName: string | null;
  communicationEmail: string;
}

export interface IPharmacyAdminListApiParams {
  adminText: string;
  pharmacyText: string;
  paging?: {
    sortBy?: string;
    desc?: boolean;
  };
}

export interface IBroadcastGroupResponse extends GenericPaginationResponse {
  group: IBroadcastGroupModel;
  data: Array<IBroadcastGroupData>;
}

export interface IAdminDashboardFilterAPIParams {
  startDate: string | null;
  endDate: string | null;
  startTime?: string | null;
  endTime?: string | null;
  daysInWeek?: Array<string>;
  softwareId?: string;
  banners?: Array<string>;
}

export interface IAdminMultipleAvailabilityCalendarParams
  extends IAdminCalendarParams {
  pharmacistId: string;
}

export interface IAdminMultipleAvailabilityCalendar extends IAdminCalendar {
  availableTimeId: string;
  isMatchingAvailabilityExist: boolean;
  start: Date;
  end: Date;
  code: string;
  pharmacistName: string;
  title: string;
  bookingState?: BookingState;
  count: number;
  replacementType?: number;
  recurrenceId: string;
  favourite?: boolean;
  professionalName?: string;
  pharmacyId: string;
  action?: string;
  isMatchingPrev: boolean;

  // ADDED FROM FRONT-END
  isDeleted: boolean;
}

export interface IAdminPharmacistVacationsDeleteParams {
  vacationIds: Array<string>;
  notes?: string;
}

export interface IPreSubmitDeleteAvailabilities {
  isAvaiabailityRequested: boolean;
}

export interface IAdminStatisticsParams {
  startDate: string;
  endDate: string;
}

export interface IGenericStatisticsResponse {
  month: number;
  data: number;
}

export interface IAdminRegisteredStatistics {
  registeredPharmacies: Array<IGenericStatisticsResponse>;
  registeredPharmacists: Array<IGenericStatisticsResponse>;
  pendigPharmacies: Array<IGenericStatisticsResponse>;
  pendingPharmacists: Array<IGenericStatisticsResponse>;
  approvedPharmacies: Array<IGenericStatisticsResponse>;
  approvedPharmacists: Array<IGenericStatisticsResponse>;
  activePharmacies: Array<IGenericStatisticsResponse>;
  activePharmacists: Array<IGenericStatisticsResponse>;
}

export interface IAdminCancellationStatistics {
  raisedByAdmin: number;
  raisedByPharmacy: number;
  raisedByPharmacist: number;
  pending: number;
  approved: number;
  rejected: number;
}

export interface IAdminFinancialStatistics {
  totalPosted: Array<IGenericStatisticsResponse>;
  totalRequested: Array<IGenericStatisticsResponse>;
  totalBooked: Array<IGenericStatisticsResponse>;
  pharmacyBilling: Array<IGenericStatisticsResponse>;
  pharmacistPayout: Array<IGenericStatisticsResponse>;
  pharmaplanProfit: Array<IGenericStatisticsResponse>;
  hours: Array<IGenericStatisticsResponse>;
  averagePharmacyRate: Array<IGenericStatisticsResponse>;
  averagePharmacistRate: Array<IGenericStatisticsResponse>;
  averageHourlyProfit: Array<IGenericStatisticsResponse>;
}

export interface IAdminWorkshiftStatistics {
  createWorkshift: Array<IGenericStatisticsResponse>;
  updateWorkshift: Array<IGenericStatisticsResponse>;
  deleteWorkshift: Array<IGenericStatisticsResponse>;
  updatePharmacy: Array<IGenericStatisticsResponse>;
  createAvailability: Array<IGenericStatisticsResponse>;
  updateAvailability: Array<IGenericStatisticsResponse>;
  deleteAvailability: Array<IGenericStatisticsResponse>;
  requestedByPharmacist: Array<IGenericStatisticsResponse>;
  requestedByProfessional: Array<IGenericStatisticsResponse>;
  updatePharmacist: Array<IGenericStatisticsResponse>;
}

export interface IAdminDailyStatistics {
  pharmacyCount: number;
  pharmacistCount: number;
  postingCount: number;
  bookingRequestCount: number;
  bookingsCount: number;
  cancellationRequestCount: number;
}

export interface IAllClientApiParams extends GenericPaginationModelAPIParams {
  userTypes: Array<HelpTypes>;
  active: boolean;
  userText?: string;
}

export interface ISingleClient {
  id: string;
  userId: string;
  userName: string;
  email: string;
  userType: HelpTypes;
}

export interface IAllClientResponse extends IGeneralPaginationResponse {
  data: Array<ISingleClient>;
}

export interface IAdminResendEmail {
  bookingId: string;
  sendEmailToPharmacist: boolean;
  sendEmailToPharmacy: boolean;
}
export interface IGenericSortingParams {
  paging?: {
    sortBy?: string;
    desc?: boolean;
  };
}

export enum PublishTypes {
  PublishPermanently = 1,
  PublishTemporarily = 2,
}

export enum AdPlatformTypes {
  All,
  PharmacyInterface,
  PharmacistInterface,
  TechnicianInterface,
  StudentInterface,
  WebsiteAboutUs,
  WebsitePharmacy,
  WebsitePharmacist,
}

export interface ICreateAdvertisementParams {
  name: string;
  platforms: string;
  publishType: string;
  expiry: string;
  url: string;
  isImageChanged: boolean;
  form: FormData | null;
  languageId: string;
}

export interface IUpdateAdvertisementParams extends ICreateAdvertisementParams {
  advertisementId: string;
}

export interface IAdminAdvertisement {
  advertisementId: string;
  name: string;
  imageName: string;
  navigateUrl: string;
  imageUrl: string;
  languageId: null | string;
  plaformTypes: Array<AdPlatformTypes>;
  publishType: PublishTypes | null;
  expiry: string | null;
  createDate: string;
}

export interface IAdvertisementPublishData {
  key: PublishTypes;
  value: string;
}

export interface IAdvertisementPlatformData {
  key: AdPlatformTypes & MiscType.All;
  value: string;
}

export interface IAdvertisementFiltersData {
  publishTypes: Array<IAdvertisementPublishData>;
  platforms: Array<IAdvertisementPlatformData>;
}

export interface IAdminTransactionsParams {
  page: number;
  sortBy?: string;
  desc?: boolean;
  transactionType?: number;
  transactionStatus?: number;
  professionalId?: string;
  startDate?: string | null;
  endDate?: string | null;
  pharmacyId?: string | null;
}

export interface IAdminTransactions {
  transactionId: string;
  transactionDate: string;
  pharmacyId: string;
  pharmacyName: string;
  professionalId: string | null;
  professionalName: string | null;
  transactionType: number;
  amount: number;
  status: number;
  refundId: string | null;
  refundDate: string | null;
  paymentId: string | null;
  paymentDate: string | null;
}

export interface IAdminTransactionsResponse extends IGeneralPaginationResponse {
  data: Array<IAdminTransactions>;
}

export interface IAdminBookingTransactions {
  bookingId: string;
  selectedDate: string;
  shiftCode: string;
  availabilityCode: string;
}

export interface IAdminTransaction {
  transactionId: string;
  transactionDate: string;
  pharmacyId: string;
  pharmacyName: string;
  professionalId: string | null;
  professionalName: string | null;
  transactionType: number;
  amount: number;
  stripeSubscriptionId: string;
  status: number;
  refundId: string | null;
  refundDate: string | null;
  paymentId: string;
  paymentDate: string;
  bookingId: string;
  workshiftCode: string | null;
  bookingDate: string | null;
  availabilityCode: string | null;
  bookingDetails?: Array<IAdminBookingTransactions>;
}

export interface IAdminTransactionFilters {
  transactionTypes: Array<IBackendDropdown>;
  transactionStatuses: Array<IBackendDropdown>;
}

export interface IAdminRefundRequests {
  refundRequestId: string;
  pharmacyName: string;
  pharmacyId: string;
  subscriptionId: string;
  email: string;
  code: string;
  requestedDate: string;
  updatedBy: string | null;
  updatedByName: string | null;
  actionDate: string | null;
  reason: string | null;
  adminReason: string | null;
  amount: number;
  refundAmount: number;
  approved: boolean;
  status: number;
}

export interface IAdminRefundRequestsResponse
  extends IGeneralPaginationResponse {
  data: Array<IAdminRefundRequests>;
}

export interface IAdminRefundActionParam {
  refundRequestId: string;
  notes?: string;
}

export interface IAdminRefundRequestParams {
  page: number;
  sortBy?: string;
  startDate?: string | null;
  endDate?: string | null;
  pharmacyId?: string;
  status?: string;
  desc?: boolean;
}

export interface IAdminRefundFilterResponse {
  statuses: Array<IBackendDropdown>;
}

export interface ISyncRecordRequest {
  isAutoSyncRecord?: boolean;
  id: string;
}

export interface IGetAdminEventsParams {
  type: AdminLegendVariant;
  selectedDate: string;
  startTime?: string;
  endTime?: string;
  banners?: Array<string>;
  softwareId?: string;
}

export interface IAdminCreateLongContract {
  pharmacyId: string;
  model: {
    shiftContractAttributes: Array<ILongContractAttributes>;
    startDate: string;
    endDate: string;
    weekDayPaidHourlyRate: number;
    weekendPaidHourlyRate: number;
    isLongTermContract?: boolean;
    otherAllowance?: number;
    allowanceNotes?: string;
    typeReplacement?: string;
    notes?: string;
  };
}

export interface IAdminRequestDetailsContractModel {
  professionalId: string;
  profilePicture: string;
  professionalName: string;
  pharmacistName: string;
  firstName: string;
  lastName: string;
  pharmacyName?: string;
  languageId: string;
  language: string;
  professionalCapacity: IProfessionalCapacity;
  phone: string;
  mobile: string;
  city: string;
  postalCode: string;
  licenseNumber: string;
  graduationYear: number;
  timezone: string;
  bookingId: string;
  bookingStateExpiration: string;
  totalWeekHours: string;
  totalWeekendHours: string;
  requests: Array<ILongContractRequestsData>;
  counterRequests?: Array<ILongContractRequestsData>;
  longTermContractId: string;
  longTermShiftContract: ILongTermContractModel;
  counterShiftContract?: ILongTermContractModel;
  requestedDate: string;
  softwares: Array<ISoftwareResponse>;
  rating: IProfessionalRating;
  isFavorite: boolean;
  pharmacyWeekDayHourlyRate?: number;
  pharmacyWeekendHourlyRate?: number;
  reviewDetails: IReviewDetails;
}

export interface IAdminApproveLongTerm {
  weekDayPaidHourlyRate: number;
  weekendPaidHourlyRate: number;
  longTermContractId: string;
  allowanceNotes?: string;
  professionalId: string;
}

export interface IAdminCompleteContract {
  contractStartDate: string;
  contractEndDate: string;
  weekDayPaidHourlyRate: number;
  weekendPaidHourlyRate: number;
  name: string;
  postalCode: string;
  profilePicture: string;
  data: Array<ILongContractRequestsData & { status?: WorkshiftStatus }>;
  allowanceNotes: string;
  proposedAllowance?: string;
  softwareName?: string;
  logoId?: string;
}

export interface IDownloadProfessionalInvoiceParams {
  bookings: Array<{ bookingId: string }>;
  professionalId: string;
}

export interface IChargesPDF {
  pdfContent: string;
  chargedDate: string;
}

export interface IInvoicePDF {
  invoiceNumber: string;
  pdfContent: string;
}

export interface IMapProfessionals {
  latitude: string;
  longitude: string;
  name: string;
  professionalId: string;
  userType: HelpTypes;
}

export interface IAdminMap {
  mapResponse: Array<IMap>;
  professionals: Array<IMapProfessionals>;
}
